import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Paper, Switch, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { AccountService } from '../../services/account.service';
import { GatewayConfigService } from '../../services/paymentLink.service';
import { GatewayRefundService } from '../../services/gatewayRefund.service'; // P1-B
import { Account } from '../../interfaces/accounting.interface';
import { GatewayConfig, GatewayMode, PaymentProvider } from '../../interfaces/sales/paymentLink.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';

const FIELD_LABELS: Record<PaymentProvider, Array<[string, string, boolean]>> = {
  BKASH: [['appKey', 'App key', false], ['appSecret', 'App secret', true], ['username', 'Username', false], ['password', 'Password', true]],
  SSLCOMMERZ: [['storeId', 'Store ID', false], ['storePassword', 'Store password', true]],
};

interface Draft {
  mode: GatewayMode; enabled: boolean; clearingAccountId: string; feeAccountId: string; feeRatePct: string;
  linkExpiryDays: string; credentials: Record<string, string>;
}

const toDraft = (c: GatewayConfig): Draft => ({
  mode: c.mode, enabled: c.enabled, clearingAccountId: c.clearingAccountId ?? '', feeAccountId: c.feeAccountId ?? '',
  feeRatePct: c.feeRatePct == null ? '' : String(c.feeRatePct), linkExpiryDays: String(c.linkExpiryDays ?? 14), credentials: {},
});

/** W3-T: per-provider gateway credentials (write-only), clearing and fee accounts, link expiry. */
const PaymentGatewaySettings: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const canManageRefunds = canManage; // P1-B: the refund opt-in is a sales setting (SALES_SETTINGS_MANAGE on the server)
  const [refundsOn, setRefundsOn] = useState<Record<string, boolean>>({}); // P1-B
  const [configs, setConfigs] = useState<GatewayConfig[]>([]);
  const [serverConfigured, setServerConfigured] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, accs] = await Promise.all([GatewayConfigService.list(institutionId), AccountService.getAllAccounts()]);
      setServerConfigured(list.serverConfigured);
      setConfigs(list.configs);
      setAccounts(accs ?? []);
      const d: Record<string, Draft> = {};
      list.configs.forEach(c => { d[c.provider] = toDraft(c); });
      setDrafts(d);
      // ---- P1-B per-provider refund switch (a failed lookup just leaves the switches off) ----
      try {
        const rs = await GatewayRefundService.settings(institutionId);
        const m: Record<string, boolean> = {};
        (Array.isArray(rs) ? rs : []).forEach(r => { m[r.provider] = r.refundsEnabled; });
        setRefundsOn(m);
      } catch { setRefundsOn({}); }
      // ---- end P1-B ----
    } catch (e) {
      toast.showApiError(e, 'Failed to load online payment settings');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    try {
      const r = await GatewayConfigService.suggestedAccounts(institutionId);
      toast.showSuccess(`${r.created.length} account(s) created, ${r.present.length} already present`);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to create the suggested accounts');
    }
  };

  const patch = (p: PaymentProvider, change: Partial<Draft>) => setDrafts(d => ({ ...d, [p]: { ...d[p], ...change } }));

  const save = async (c: GatewayConfig) => {
    const d = drafts[c.provider];
    if (!d) return;
    try {
      setSaving(c.provider);
      const credentials: Record<string, string> = {};
      Object.entries(d.credentials).forEach(([k, v]) => { if (v.trim()) credentials[k] = v.trim(); });
      await GatewayConfigService.save({
        institutionId, provider: c.provider, mode: d.mode, enabled: d.enabled,
        credentials: Object.keys(credentials).length ? credentials : undefined,
        clearingAccountId: d.clearingAccountId || null, feeAccountId: d.feeAccountId || null,
        feeRatePct: d.feeRatePct === '' ? null : Number(d.feeRatePct), linkExpiryDays: Number(d.linkExpiryDays) || 14,
      });
      toast.showSuccess(`${c.providerLabel ?? c.provider} settings saved`);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the settings');
    } finally {
      setSaving(null);
    }
  };

  // ---- P1-B ----
  const toggleRefunds = async (provider: PaymentProvider, on: boolean) => {
    const before = refundsOn;
    setRefundsOn(m => ({ ...m, [provider]: on }));
    try {
      const rs = await GatewayRefundService.saveSetting(institutionId, { provider, refundsEnabled: on });
      const m: Record<string, boolean> = {};
      (Array.isArray(rs) ? rs : []).forEach(r => { m[r.provider] = r.refundsEnabled; });
      setRefundsOn(m);
    } catch (e) {
      setRefundsOn(before);
      toast.showApiError(e, 'Failed to save the refund setting');
    }
  };
  // ---- end P1-B ----

  const assets = leafAccounts(accounts.filter(a => a.accountType === 'ASSET'));
  const expenses = leafAccounts(accounts.filter(a => a.accountType === 'EXPENSE'));

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  return (
    <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="sales.gatewaySettings.page">
      <Typography variant="h5" gutterBottom>Online Payments Settings</Typography>
      {!serverConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Online payments are switched off on this server (PAYMENT_CREDENTIALS_KEY is not set), so credentials cannot be saved yet.
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        Credentials are write-only: once saved they are encrypted and never shown again. Leave a field blank to keep what is stored.
        SANDBOX mode (the provider's sandbox credentials) is accepted only on servers that enable it (staging): a sandbox payment still books a real receipt in the books, so it is refused on production.
      </Alert>
      {canManage && <Button data-help="sales.gatewaySettings.suggest" variant="outlined" sx={{ mb: 2 }} onClick={suggest}>Create suggested accounts</Button>}
      {configs.map(c => {
        const d = drafts[c.provider];
        if (!d) return null;
        return (
          <Paper key={c.provider} sx={{ p: 2, mb: 2 }} data-help-section="sales.gatewaySettings.section.provider">
            <Typography variant="h6" gutterBottom>{c.providerLabel ?? c.provider}</Typography>
            {d.mode === 'SANDBOX' && <Alert severity="warning" sx={{ mb: 2 }}>SANDBOX: no real money moves in this mode.</Alert>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <TextField data-help="sales.gatewaySettings.mode" select label="Mode" size="small" value={d.mode} disabled={!canManage}
                sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => patch(c.provider, { mode: e.target.value as GatewayMode })}>
                <MenuItem value="SANDBOX">Sandbox</MenuItem><MenuItem value="LIVE">Live</MenuItem>
              </TextField>
              <TextField data-help="sales.gatewaySettings.linkExpiry" label="Link expiry (days)" type="number" size="small" value={d.linkExpiryDays} disabled={!canManage}
                sx={{ flex: '1 1 140px', maxWidth: 200 }} onChange={e => patch(c.provider, { linkExpiryDays: e.target.value })} />
              <TextField data-help="sales.gatewaySettings.feeRate" label="Fee rate % (pre-fills payouts)" type="number" size="small" value={d.feeRatePct} disabled={!canManage}
                sx={{ flex: '1 1 180px', maxWidth: 240 }} onChange={e => patch(c.provider, { feeRatePct: e.target.value })} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {FIELD_LABELS[c.provider].map(([key, label, secret]) => (
                <TextField key={key} data-help="sales.gatewaySettings.credential" label={label} size="small" type={secret ? 'password' : 'text'} autoComplete="off"
                  disabled={!canManage || !serverConfigured}
                  placeholder={c.credentialFields?.[key] ? 'Saved - leave blank to keep' : ''}
                  helperText={key === 'appKey' || key === 'storeId' ? (c.identifierHint ? `Current: ${c.identifierHint}` : undefined) : undefined}
                  value={d.credentials[key] ?? ''} sx={{ flex: '1 1 220px', maxWidth: 320 }}
                  onChange={e => patch(c.provider, { credentials: { ...d.credentials, [key]: e.target.value } })} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Box sx={{ flex: '1 1 280px', maxWidth: 420 }}>
                <AccountAutocomplete data-help="sales.gatewaySettings.clearingAccount" label="Gateway clearing account (asset)" accounts={assets} value={d.clearingAccountId}
                  onChange={id => patch(c.provider, { clearingAccountId: id })} placeholder="None" />
              </Box>
              <Box sx={{ flex: '1 1 280px', maxWidth: 420 }}>
                <AccountAutocomplete data-help="sales.gatewaySettings.feeAccount" label="Gateway fee account (expense)" accounts={expenses} value={d.feeAccountId}
                  onChange={id => patch(c.provider, { feeAccountId: id })} placeholder="None" />
              </Box>
            </Box>
            {/* ---- P1-B ---- */}
            <Box sx={{ mb: 1 }}>
              <FormControlLabel data-help="sales.gatewaySettings.refunds" control={<Switch checked={!!refundsOn[c.provider]} disabled={!canManageRefunds}
                onChange={e => toggleRefunds(c.provider, e.target.checked)} />} label="Allow refunds through this gateway" />
              <Typography variant="body2" color="text.secondary">
                Sandbox refunds only in this release; live refunds stay off until enabled on the server.
              </Typography>
            </Box>
            {/* ---- end P1-B ---- */}
            <FormControlLabel data-help="sales.gatewaySettings.enabled" control={<Checkbox checked={d.enabled} disabled={!canManage}
              onChange={e => patch(c.provider, { enabled: e.target.checked })} />} label="Enabled" />
            {canManage && <Button data-help="sales.gatewaySettings.save" variant="contained" sx={{ ml: 2 }} disabled={saving === c.provider || (!serverConfigured && Object.keys(d.credentials).length > 0)}
              onClick={() => save(c)}>Save</Button>}
          </Paper>
        );
      })}
    </Box>
  );
};

export default PaymentGatewaySettings;
